/**
 * Admin Registrar Drift Service (P3)
 *
 * Reads the registrar-drift list and resolves drift rows.
 * Backend routes:
 *   GET  /api/v1/domain-admin/drifts?resolved=&page=&pageSize=
 *   POST /api/v1/domain-admin/drifts/{id}/resolve
 */

import { apiClient } from "@/lib/api";
import type {
  PaginatedStandardResponse,
  PaginationInfo,
  StandardResponse,
} from "@/types/api-response";
import { extractData, extractPaginatedData } from "@/types/api-response";
import type {
  RegistrarDriftDto,
  RegistrarDriftFilters,
  ResolveDomainDriftRequest,
  ResolveDomainDriftResponse,
} from "@/types/domain-drift";

export const adminRegistrarDriftService = {
  /**
   * List drift rows. The dashboard defaults to `resolved=false`; pass
   * `undefined` to include both resolved and unresolved rows.
   */
  async list(filters: RegistrarDriftFilters): Promise<{
    data: RegistrarDriftDto[];
    pagination: PaginationInfo;
  }> {
    const params = new URLSearchParams();
    if (filters.resolved !== undefined) {
      params.append("resolved", String(filters.resolved));
    }
    params.append("page", filters.page.toString());
    params.append("pageSize", filters.pageSize.toString());

    const response = await apiClient.get<
      PaginatedStandardResponse<RegistrarDriftDto>
    >(`/api/v1/domain-admin/drifts?${params.toString()}`);
    return extractPaginatedData(response.data);
  },

  /**
   * Resolve one drift row with an optional notes blob (audit trail).
   * Returns the full updated drift row.
   */
  async resolve(
    id: string,
    body: ResolveDomainDriftRequest,
  ): Promise<ResolveDomainDriftResponse> {
    const response = await apiClient.post<
      StandardResponse<ResolveDomainDriftResponse>
    >(`/api/v1/domain-admin/drifts/${id}/resolve`, body);
    return extractData(response.data);
  },
};
